import { apiUrl } from "../../api/core/apiUrl";
import { PageContent } from "../../components/core/PageContent/PageContent";
import { Board } from "../../types/app/board.types";
import {} from "./BoardForm";
import http from "../../api/core/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Loader } from "../../components/core/Loader/Loader";
import { BoardContentList } from "../BoardContent/BoardContentList";
import { BoardOwner } from "../../types/app/boardOwner.types";
import { BoardButton as BoardButtonModel } from "../../types/app/boardButton.types";
import { useAuth } from "../../contexts/authContext";
import { Box, Button, Grid, Typography } from "@mui/material";
import {
  fullscreenPathPrefix,
  pathFullscreen,
} from "../../utils/core/routes";
import { theme } from "../../App";
import { BoardButtonBar } from "./BoardButtonBar";

// Výška oranžového pruhu s nadpisem a logem. Lišta tlačítek se přidává pod něj,
// takže celková výška hlavičky je proměnlivá a měří se za běhu.
const FULLSCREEN_HEADLINE_HEIGHT = 65;

export function BoardDetail() {
  const { pathname } = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [data, setData] = useState<Board | null>(null);
  const [ownerList, setOwnerList] = useState<BoardOwner[] | null>(null);
  const [buttons, setButtons] = useState<BoardButtonModel[]>([]);
  const [headerHeight, setHeaderHeight] = useState<number>(
    FULLSCREEN_HEADLINE_HEIGHT
  );
  const headerObserverRef = useRef<ResizeObserver | null>(null);
  const isFullScreen = pathname.includes(fullscreenPathPrefix);

  // Obsah se odsazuje podle skutečně naměřené výšky hlavičky. Napevno zadané
  // číslo tu nestačí — hlavička je jednou vysoká jen o nadpis, jindy o nadpis
  // i lištu tlačítek.
  const headerRef = useCallback((element: HTMLDivElement | null) => {
    headerObserverRef.current?.disconnect();
    headerObserverRef.current = null;
    if (!element) return;
    const observer = new ResizeObserver(() =>
      setHeaderHeight(element.offsetHeight)
    );
    observer.observe(element);
    headerObserverRef.current = observer;
    setHeaderHeight(element.offsetHeight);
  }, []);

  const fetchBoard = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_ITEM}?id=${id}`,
    });
    setData(response?.data);
  };

  const fetchBoardOwners = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_OWNER_LIST}?parentId=${id}`,
    });
    setOwnerList(response?.data);
  };

  const fetchBoardButtons = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_BUTTON_LIST}?parentId=${id}`,
    });
    setButtons(response?.data ?? []);
  };

  useEffect(() => {
    fetchBoard();
    fetchBoardOwners();
    fetchBoardButtons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // pokud jsem ve fullscreenu nebo nejsem owner, tak readonly
    if (!ownerList || pathname.includes(fullscreenPathPrefix)) return;
    if (!user) return setReadOnly(true);
    let owner = ownerList.find((owner: BoardOwner) => owner.userId === user.id);
    if (owner) setReadOnly(false);
  }, [ownerList, user, pathname]);

  // Otevře fullscreen board detailu v novém okně
  const handleOpenFullScreen = () => {
    window.open(
      `${pathFullscreen.BOARD_DETAIL}/${id}`,
      "toolbar=no,resizable=yes,location=yes"
    );
  };

  // refresh stránky každých 5min
  setInterval(function () {
    window.location.reload();
  }, 300000);

  if (!data || !ownerList) return <Loader />;

  return (
    <>
      {isFullScreen && (
        <Box
          ref={headerRef}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: (muiTheme) => muiTheme.zIndex.appBar,
          }}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              color: "#fff",
              height: FULLSCREEN_HEADLINE_HEIGHT,
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 2,
            }}
          >
            <Typography
              variant={"h4"}
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.headline}
            </Typography>
            <Box
              component={"img"}
              src={"/logo/te-logo-white.png"}
              alt={"Application logo"}
              title={"Application logo"}
              sx={{ flex: "0 0 auto", height: 40 }}
            />
          </Box>
          <BoardButtonBar buttons={buttons} isFullScreen={isFullScreen} />
        </Box>
      )}

      <Grid
        item
        flex={1}
        sx={{ marginTop: isFullScreen ? `${headerHeight}px` : 0 }}
      >
        {!isFullScreen && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              <Typography
                variant={"h4"}
                color={theme.palette.primary.main}
                sx={{ flex: "1 1 auto", minWidth: 0 }}
              >
                {data.headline}
              </Typography>
              <Button
                variant="contained"
                onClick={() => handleOpenFullScreen()}
                sx={{ flex: "0 0 auto", minHeight: 44 }}
              >
                Fullscreen
              </Button>
            </Box>
            <BoardButtonBar buttons={buttons} isFullScreen={isFullScreen} />
          </Grid>
        )}
        <PageContent>
          <BoardContentList readOnly={readOnly} board={data} />
        </PageContent>
      </Grid>
    </>
  );
}
